/**
 * Single source of truth for deciding whether a secure-posture task is a hub task
 * or a legacy probe (ManagementTasks) task.
 *
 * Invariant: a posture whose definition is hub-capable (any property or
 * configureProperty declares BOTH `hubTaskType` and `hubAction`) MUST be routed to
 * the integration hub and must never be written to the ManagementTasks table. Only
 * definitions with no hub metadata anywhere are legitimate legacy probe tasks.
 *
 * Routing used to be decided per-setting from fragile title-matching, which silently
 * dropped hub metadata and leaked hub tasks into ManagementTasks (AP-13051). Deciding
 * from the definition removes that failure mode. There are no "hybrid" definitions
 * (none mix hub-capable and non-hub-capable properties), so a whole-task decision is safe.
 */

const getHubCandidateProperties = (postureDefinition) => [
  ...(postureDefinition?.properties || []),
  ...(postureDefinition?.configureProperties || []),
]

/**
 * @param {object} postureDefinition A preTaskDefinitions entry.
 * @returns {{ hubTaskType: string, hubAction: string } | null} Hub routing metadata, or
 * null when the definition is a legacy probe task.
 */
export const getPostureHubTaskMeta = (postureDefinition) => {
  const hubProperty = getHubCandidateProperties(postureDefinition).find(
    (property) => property?.hubTaskType && property?.hubAction,
  )

  if (!hubProperty) {
    return null
  }

  return { hubTaskType: hubProperty.hubTaskType, hubAction: hubProperty.hubAction }
}

/**
 * Transforms the flat `{ propertyName: value }` actions into the payload shape a hub
 * handler expects, when the definition declares a `formatHubActions` transform (e.g.
 * authentication methods policy, which needs `authenticationMethodConfigurations`).
 * Definitions without a transform pass their actions through unchanged.
 *
 * @param {object} postureDefinition A preTaskDefinitions entry.
 * @param {object} actions The flat actions built from the definition's properties.
 * @returns {object} Actions ready to send to `createHubTask`.
 */
export const formatHubActions = (postureDefinition, actions) =>
  typeof postureDefinition?.formatHubActions === 'function' ? postureDefinition.formatHubActions(actions) : actions
